/**
 * The one answer to "does Stripe want anything from this merchant".
 *
 * This exists because the question was previously answered in four places that
 * could not all be right at once: a gate in `App.tsx`, a permanent nav item, a
 * warning on the overview, and a page of checklists that went on describing an
 * unfinished verification after it had finished. A merchant with a fully
 * verified account was still shown a form asking them to verify it.
 *
 * So the rule lives here, once, and every surface reads it. When this returns
 * `null` there is nothing to say and nothing may be rendered - no banner, no nav
 * item, no page. Silence is the correct output of a healthy account.
 *
 * The inputs are the server's, not the client's: `onboarding_complete` is
 * computed from `charges_enabled`, which only Stripe sets, and `requirements_due`
 * is Stripe's own list copied verbatim. Nothing here decides anything.
 */

export type AccountNeed =
  /** Charges are not enabled. The merchant cannot trade at all yet. */
  | 'verification'
  /** Charges work, but Stripe has asked for something since. */
  | 'requirements'
  /** Nothing outstanding. Say nothing. */
  | null;

/** The shape this needs, kept structural so `Overview['merchant']` satisfies it. */
export interface AccountFacts {
  onboarding_complete: boolean;
  requirements_due: string[];
}

export function accountNeed(merchant: AccountFacts): AccountNeed {
  if (!merchant.onboarding_complete) return 'verification';
  if (merchant.requirements_due.length > 0) return 'requirements';
  return null;
}

/**
 * How often to re-ask, given where the account stands.
 *
 * Polling used to stop the moment the gate lifted, which left the panel unable
 * to notice a requirement that arrived afterwards - an expired document, a new
 * threshold - until the merchant happened to reload. Stripe raises those without
 * switching charges off, so nothing else would have prompted a reload either.
 *
 * Both figures read our own record rather than Stripe: one small query against a
 * row the `account.updated` webhook already keeps current, so the slow poll on a
 * healthy account costs almost nothing and the fast one is affordable.
 */
export function pollInterval(need: AccountNeed): number {
  return need === null ? 120_000 : 20_000;
}
